export type ReportRequest = {
  start_date: string; end_date: string; timezone: string;
  property_id: number | null; client_id: number | null; source: string | null; include_history: boolean;
};
export type OutcomeLead = {
  lead_id: number; property_name: string | null; domain: string | null; occurred_at: string;
  source_system: string; delivery_status: string; outcome_revision: number;
  contacted_at: string | null; qualification: "unknown" | "qualified" | "unqualified";
  appointment_at: string | null; appointment_status: "unknown" | "booked" | "attended" | "canceled" | "no_show";
  sale_status: "unknown" | "won" | "lost"; revenue_amount: number | null;
  reason: string | null; evidence_reference: string | null; notes: string | null;
};
export type PerformanceProperty = {
  property_id: number | null; property_name: string; domain: string | null; client_name: string | null;
  inquiries: number; forms: number; client_sent: number; internal_only: number; qualified: number;
  qualification_recorded: number; contacted: number; booked: number; appointment_recorded: number; won: number; sales_recorded: number;
  revenue: number | null; calls: number; answered: number; unanswered: number; unlinked: number;
};
export type PlatformSnapshot = {
  snapshot_key: string; provider: string; account_id: string; period_start: string; period_end: string;
  report_timezone: string; captured_at: string; source_url: string;
  evidence: Record<string, unknown>;
};
export type FactoryPerformanceData = {
  generated_at: string;
  period: { start_date: string; end_date: string; timezone: string };
  filters: { property_id: number | null; client_id: number | null; source: string | null; include_history: boolean };
  can_record_outcomes: boolean;
  totals: {
    inquiries: number; forms: number; client_assigned: number; client_sent: number; internal_only: number;
    delivery_unverified: number; delivery_failed: number; contacted: number; qualification_recorded: number;
    qualified: number; unqualified: number; outcome_unknown: number; booked: number; appointment_recorded: number; attended: number;
    won: number; sales_recorded: number; revenue: number | null; historical_excluded: number;
    incomplete_calls_excluded: number; calls_without_event: number;
  };
  calls: {
    total: number; first_time: number; answered: number; missed: number; abandoned: number; voicemail: number;
    unlinked: number; google_ads: number; scored: number; incomplete: number; source_first_observed: string | null;
  };
  sources: Array<{ source_system: string; inquiries: number; qualified: number }>;
  call_sources: Array<{ provider_source: string; calls: number; answered: number; unlinked: number }>;
  properties: PerformanceProperty[];
  daily: Array<{ date: string; inquiries: number; forms: number }>;
  lead_queue: OutcomeLead[];
  unlinked_calls: Array<{ event_id: string; call_id: string; company_name: string; occurred_at: string; call_status: string; provider_source: string; mapping_status: string }>;
  health: Array<{ provider: string; status: string; last_attempt_at: string | null; last_success_at: string | null; records_received: number; failure_details: unknown; source_observed_at: string | null }>;
  ad_metrics: { rows: number; coverage: string; spend: number | null; cost_per_qualified_lead: number | null; cost_per_booked_job: number | null; return_on_ad_spend: number | null };
  snapshots: PlatformSnapshot[];
  review_items: Array<{ issue_key: string; title: string; detail: string; evidence_reference: string; observed_at: string; status: string }>;
  verification: { last_channel_test: string | null; last_channel_verified: string | null };
};

export function completedPeriod(days = 30, timezone = "America/Chicago", now = new Date()): ReportRequest {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const get = (type: string) => parts.find((part) => part.type === type)!.value;
  const end = new Date(get("year") + "-" + get("month") + "-" + get("day") + "T12:00:00Z");
  end.setUTCDate(end.getUTCDate() - 1);
  const start = new Date(end); start.setUTCDate(start.getUTCDate() - days + 1);
  return { start_date: start.toISOString().slice(0, 10), end_date: end.toISOString().slice(0, 10), timezone, property_id: null, client_id: null, source: null, include_history: false };
}
export function recordedValue(value: number, recorded: number): string {
  return recorded > 0 ? new Intl.NumberFormat("en-US").format(value) : "Not recorded";
}
export function csvCell(value: unknown): string {
  let text = value == null ? "" : String(value);
  if (/^\s*[=+\-@\t\r]/.test(text)) text = "'" + text;
  return '"' + text.replaceAll('"', '""') + '"';
}
export function propertyCsv(rows: PerformanceProperty[], report: FactoryPerformanceData): string {
  const header = ["Start date", "End date", "Timezone", "Property", "Domain", "Client", "Lead records", "Forms", "Calls", "Calls without lead", "Client sent", "Internal only", "Qualified", "Qualification recorded", "Contacted", "Booked", "Appointment outcomes recorded", "Won", "Sales recorded", "Recorded revenue USD"];
  return [header, ...rows.map((r) => [report.period.start_date, report.period.end_date, report.period.timezone, r.property_name, r.domain, r.client_name, r.inquiries, r.forms, r.calls, r.unlinked, r.client_sent, r.internal_only, r.qualification_recorded ? r.qualified : null, r.qualification_recorded, r.contacted, r.appointment_recorded ? r.booked : null, r.appointment_recorded, r.sales_recorded ? r.won : null, r.sales_recorded, r.revenue])].map((row) => row.map(csvCell).join(",")).join("\r\n");
}
export function localDateTime(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
export function isoDateTime(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}
